import Image from '@tiptap/extension-image';
import { common, createLowlight } from 'lowlight';

// Extend the type for setImage command to accept 'size'
declare module '@tiptap/extension-image' {
  interface ImageOptions {
    size?: 'small' | 'medium' | 'large';
  }
}
import LinkExtension from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { mergeClasses } from 'minimal-shared/utils';
import StarterKitExtension from '@tiptap/starter-kit';
import TextAlignExtension from '@tiptap/extension-text-align';
import PlaceholderExtension from '@tiptap/extension-placeholder';
import { useState, useEffect, forwardRef, useCallback } from 'react';
import CodeBlockLowlightExtension from '@tiptap/extension-code-block-lowlight';
import { useEditor, EditorContent, ReactNodeViewRenderer } from '@tiptap/react';

import Box from '@mui/material/Box';
import Portal from '@mui/material/Portal';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormHelperText from '@mui/material/FormHelperText';

import { Toolbar } from './toolbar';
import { EditorRoot } from './styles';
import { editorClasses } from './classes';
import { CodeHighlightBlock } from './components/code-highlight-block';

import type { EditorProps } from './types';

// ✅ CustomImage extension with dynamic size class
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      size: {
        default: 'medium',
        parseHTML: (element) => element.getAttribute('data-size') || 'medium',
        renderHTML: (attributes) => ({
          'data-size': attributes.size,
          class: `image-${attributes.size}`,
        }),
      },
    };
  },
});

// ✅ Inject image size CSS globally
const imageStyles = document.createElement('style');
imageStyles.innerHTML = `
.image-small { max-width: 150px; height: auto; }
.image-medium { max-width: 300px; height: auto; }
.image-large { max-width: 600px; height: auto; }
`;
document.head.appendChild(imageStyles);

// ----------------------------------------------------------------------

export const Editor = forwardRef<HTMLDivElement, EditorProps>((props, ref) => {
  const {
    sx,
    error,
    onChange,
    slotProps,
    helperText,
    resetValue,
    className,
    editable = true,
    fullItem = false,
    value: content = '',
    placeholder = 'Write something awesome...',
    direction = 'rtl',
    isEmailEditor = false,
    isSignatureEditor = false,
    ...other
  } = props;

  const [fullScreen, setFullScreen] = useState(false);

  const handleToggleFullScreen = useCallback(() => setFullScreen((prev) => !prev), []);

  const lowlight = createLowlight(common);

  const editor = useEditor({
    content,
    editable,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    extensions: [
      Underline,
      StarterKitExtension.configure({
        codeBlock: false,
        code: { HTMLAttributes: { class: editorClasses.content.codeInline } },
        heading: { HTMLAttributes: { class: editorClasses.content.heading } },
        horizontalRule: { HTMLAttributes: { class: editorClasses.content.hr } },
        listItem: { HTMLAttributes: { class: editorClasses.content.listItem } },
        blockquote: { HTMLAttributes: { class: editorClasses.content.blockquote } },
        bulletList: { HTMLAttributes: { class: editorClasses.content.bulletList } },
        orderedList: { HTMLAttributes: { class: editorClasses.content.orderedList } },
      }),
      PlaceholderExtension.configure({
        placeholder,
        emptyEditorClass: editorClasses.content.placeholder,
      }),
      CustomImage.configure(), // ✅ Allow dynamic class from attributes
      TextAlignExtension.configure({ types: ['heading', 'paragraph'] }),
      LinkExtension.configure({
        autolink: true,
        openOnClick: false,
        HTMLAttributes: { class: editorClasses.content.link },
      }),
      CodeBlockLowlightExtension.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeHighlightBlock);
        },
      }).configure({ lowlight, HTMLAttributes: { class: editorClasses.content.codeBlock } }),
    ],
    onUpdate({ editor: _editor }) {
      const html = _editor.getHTML();
      const text = _editor.getText();
      const textAlignments = ['left', 'center', 'justify', 'right'];

      const isFiltered =
        _editor.isActive('bold') ||
        _editor.isActive('italic') ||
        _editor.isActive('underline') ||
        _editor.isActive('bulletList') ||
        _editor.isActive('orderedList') ||
        textAlignments.some((align) => _editor.isActive({ textAlign: align })) ||
        Array.from({ length: 6 }, (_, i) => i + 1).some((level) =>
          _editor.isActive('heading', { level })
        );

      console.log('🚀 ~ onUpdate ~ html:', html);
      onChange?.(isEmailEditor ? html : isFiltered ? html : text);
    },
    ...other,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (editor?.isEmpty && content !== '<p></p>') {
        console.log('🚀 ~ timer ~ content:', content);
        editor.commands.setContent(content);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [content, editor]);

  useEffect(() => {
    if (resetValue && !content) {
      editor?.commands.clearContent();
    }
  }, [content]);

  useEffect(() => {
    document.body.style.overflow = fullScreen ? 'hidden' : '';
  }, [fullScreen]);

  return (
    <Portal disablePortal={!fullScreen}>
      {fullScreen && <Backdrop open sx={[(theme) => ({ zIndex: theme.zIndex.modal - 1 })]} />}

      <Box
        {...slotProps?.wrapper}
        sx={[
          () => ({
            display: 'flex',
            flexDirection: 'column',
            ...(!editable && { cursor: 'not-allowed' }),
          }),
          ...(Array.isArray(slotProps?.wrapper?.sx)
            ? (slotProps?.wrapper?.sx ?? [])
            : [slotProps?.wrapper?.sx]),
        ]}
      >
        <EditorRoot
          error={!!error}
          disabled={!editable}
          fullScreen={fullScreen}
          className={mergeClasses([editorClasses.root, className])}
          sx={sx}
          dir={direction}
        >
          <Toolbar
            editor={editor}
            fullItem={fullItem}
            fullScreen={fullScreen}
            onToggleFullScreen={handleToggleFullScreen}
            isSignatureEditor={isSignatureEditor}
          />

          {/* ✅ Image Size Buttons */}
          {editor && isSignatureEditor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Size Buttons */}
              {editor?.isActive('image') && (
                <ButtonGroup size="small">
                  <Button
                    onClick={() => editor.commands.updateAttributes('image', { size: 'small' })}
                  >
                    Small
                  </Button>
                  <Button
                    onClick={() => editor.commands.updateAttributes('image', { size: 'medium' })}
                  >
                    Medium
                  </Button>
                  <Button
                    onClick={() => editor.commands.updateAttributes('image', { size: 'large' })}
                  >
                    Large
                  </Button>
                </ButtonGroup>
              )}
            </Box>
          )}

          <EditorContent
            ref={ref}
            spellCheck="false"
            autoComplete="off"
            autoCapitalize="off"
            editor={editor}
            className={editorClasses.content.root}
            dir={direction}
          />
        </EditorRoot>

        {helperText && (
          <FormHelperText error={!!error} sx={{ px: 2 }}>
            {helperText}
          </FormHelperText>
        )}
      </Box>
    </Portal>
  );
});
